import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  Image,
  ActivityIndicator,
  Text as RNText,
} from 'react-native';
import { 
  PageContainer, 
  ContentContainer,
  BottomContainer,
  Text, 
  Button,
  Theme 
} from '../../../components/ui';

interface LearnByDoingPageProps {
  page: any;
  onAnswer: (answer: string) => void;
  isLoading?: boolean;
  onStartLoading?: () => void;
}

export default function LearnByDoingPage({ page, onAnswer, isLoading = false, onStartLoading }: LearnByDoingPageProps) {
  const [buttonPressed, setButtonPressed] = useState(false);
  const [hasStartedLoading, setHasStartedLoading] = useState(false);

  // Start loading questions in background when component mounts (only once)
  useEffect(() => {
    if (!hasStartedLoading && onStartLoading) {
      console.log('LearnByDoingPage useEffect - starting loading');
      setHasStartedLoading(true);
      onStartLoading();
    }
  }, []); // Empty dependency array - run only once on mount

  const handleContinue = () => {
    if (!isLoading) {
      setButtonPressed(true);
      onAnswer('continue');
    }
  };

  return (
    <PageContainer>
      <ContentContainer scrollable={true}>
        {/* Learn by Doing Banner */}
        <View style={styles.bannerContainer}>
          <View style={styles.banner}>
            <Text style={styles.bannerText}>
              💪 Learn by Doing 💪
            </Text>
          </View>
        </View>

        {/* Image */}
        <View style={styles.imageContainer}>
          <Image 
            source={require('../../../assets/onboarding/books-vs-summaries.webp')} 
            style={styles.comparisonImage}
            resizeMode="contain"
          />
        </View>

        {/* Reading vs Doing Comparison */}
        <View style={styles.comparisonContainer}>
          {/* Reading Column */}
          <View style={styles.comparisonColumn}>
            <View style={styles.comparisonHeader}>
              <Text style={styles.readingTitle}>Reading</Text>
              <View style={styles.thumbsDownContainer}>
                <Text style={styles.thumbsDown}>👎</Text>
              </View>
            </View>
            <View style={styles.featuresList}>
              <Text style={styles.readingFeature}>Passive consumption</Text>
              <Text style={styles.readingFeature}>No practical application</Text>
              <Text style={styles.readingFeature}>Knowledge fades quickly</Text>
            </View>
          </View>

          {/* VS Divider */}
          <View style={styles.vsContainer}>
            <Text style={styles.vsText}>VS</Text>
          </View>

          {/* Doing Column */}
          <View style={styles.comparisonColumn}>
            <View style={styles.comparisonHeader}>
              <Text style={styles.doingTitle}>Doing</Text>
              <View style={styles.thumbsUpContainer}>
                <Text style={styles.thumbsUp}>👍</Text>
              </View>
            </View>
            <View style={styles.featuresList}>
              <View style={styles.doingFeature}>
                <Text style={styles.checkmark}>✓</Text>
                <Text style={styles.doingFeatureText}>Active implementation</Text>
              </View>
              <View style={styles.doingFeature}>
                <Text style={styles.checkmark}>✓</Text>
                <Text style={styles.doingFeatureText}>Real-world application</Text>
              </View>
              <View style={styles.doingFeature}>
                <Text style={styles.checkmark}>✓</Text>
                <Text style={styles.doingFeatureText}>Lasting transformation</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Main Title */}
        <View style={styles.titleContainer}>
          <RNText style={styles.mainTitle}>
            Mentorist helps you
          </RNText>
          <RNText style={styles.mainTitle}>
            <RNText style={styles.highlightedText}>achieve your goals</RNText> faster
          </RNText>
        </View>
      </ContentContainer>

      <BottomContainer>
        <Button
          title={isLoading ? "Loading questions..." : "Continue"}
          onPress={handleContinue}
          disabled={isLoading}
          style={styles.continueButton}
        >
          {isLoading && (
            <ActivityIndicator size="small" color={Theme.colors.textInverse} style={styles.loadingSpinner} />
          )}
        </Button>
      </BottomContainer>
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  bannerContainer: {
    alignItems: 'center',
    marginBottom: Theme.spacing.xxl,
  },
  banner: {
    backgroundColor: Theme.colors.surface,
    borderRadius: Theme.borderRadius.round,
    paddingHorizontal: Theme.spacing.xxl,
    paddingVertical: Theme.spacing.md,
    borderWidth: 1,
    borderColor: Theme.colors.primaryLight,
    shadowColor: Theme.colors.textPrimary,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  bannerText: {
    fontSize: 18,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.textPrimary,
  },
  imageContainer: {
    alignItems: 'center',
    marginBottom: Theme.spacing.xxl,
  },
  comparisonImage: {
    width: '100%',
    maxWidth: 320,
    height: 240,
  },
  comparisonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingHorizontal: Theme.spacing.lg,
    marginBottom: Theme.spacing.xxxxl,
    gap: Theme.spacing.sm,
  },
  comparisonColumn: {
    flex: 1,
  },
  comparisonHeader: {
    alignItems: 'center',
    marginBottom: Theme.spacing.lg,
  },
  readingTitle: {
    fontSize: 20,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: '#ef4444', // red color for reading
    marginBottom: Theme.spacing.sm,
    textAlign: 'center',
  },
  doingTitle: {
    fontSize: 20,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.primary,
    marginBottom: Theme.spacing.sm,
    textAlign: 'center',
  },
  thumbsDownContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#ef4444',
    justifyContent: 'center',
    alignItems: 'center',
  },
  thumbsUpContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: Theme.colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  thumbsDown: {
    fontSize: 18,
  },
  thumbsUp: {
    fontSize: 18,
  },
  featuresList: {
    gap: Theme.spacing.sm,
  },
  readingFeature: {
    fontSize: 14,
    lineHeight: 20,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textSecondary,
    textAlign: 'left',
  },
  doingFeature: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Theme.spacing.sm,
  },
  checkmark: {
    fontSize: 14,
    color: Theme.colors.primary,
    fontWeight: 'bold',
  },
  doingFeatureText: {
    fontSize: 14,
    lineHeight: 20,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.primary,
    flex: 1,
  },
  vsContainer: {
    paddingHorizontal: Theme.spacing.md,
    justifyContent: 'center',
    alignItems: 'center',
  },
  vsText: {
    fontSize: 24,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.textSecondary,
  },
  titleContainer: {
    alignItems: 'center',
    paddingHorizontal: Theme.spacing.lg,
    marginTop: Theme.spacing.xl,
    gap: Theme.spacing.sm,
  },
  mainTitle: {
    fontSize: 34,
    lineHeight: 42,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textPrimary,
    textAlign: 'center',
  },
  highlightedText: {
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.primary,
  },
  continueButton: {
    minHeight: 56,
  },
  loadingSpinner: {
    marginLeft: Theme.spacing.sm,
  },
}); 